/**
 * Salesforce Conversation Service
 * Handles conversation creation, messaging, and management
 */

import { SALESFORCE_CONSTANTS } from './constants';
import { getCurrentJwt } from './authService';
import { 
  getMessagingUrl, 
  getDeploymentDeveloperName,
  getOrganizationId,
  storeConversationId, 
  getConversationId,
  clearConversationId,
  getJwt
} from './webStorageService';

/**
 * Send authenticated request to Salesforce API with automatic JWT refresh on 403
 */
const sendAuthenticatedRequest = async (url, options = {}) => {
  const jwt = getCurrentJwt();
  
  if (!jwt) {
    console.error('[Salesforce:Conversation] JWT token missing');
    throw new Error('No JWT token available for authenticated request. Ensure authentication is completed before making API calls.');
  }

  const defaultOptions = {
    mode: 'cors',
    headers: {
      'Authorization': `Bearer ${jwt}`,
      'Content-Type': 'application/json',
      ...options.headers
    },
    ...options
  };

  try {
    const response = await fetch(url, defaultOptions);

    // Check for specific error response
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { message: 'Unknown error', status: response.status };
      }

      console.error('[Salesforce:Conversation] API Error:', response.status);
      console.error('[Salesforce:Conversation] Error data:', JSON.stringify(errorData));
      console.error('[Salesforce:Conversation] URL:', url);
      
      const error = new Error(`Salesforce API Error: ${response.status} - ${JSON.stringify(errorData)}`);
      error.status = response.status;
      error.errorData = errorData;
      throw error;
    }

    // Check if response has content before parsing JSON
    // Some endpoints (like typing indicators) return 204 No Content
    const contentType = response.headers.get('content-type');
    const hasJsonContent = contentType && contentType.includes('application/json');
    
    // Only parse JSON if there's content
    if (response.status === 204 || !hasJsonContent) {
      return { success: true };
    }

    // Parse JSON response
    const data = await response.json();
    return data;
    
  } catch (error) {
    console.error('[Salesforce:Conversation] Request failed:', error);
    throw error;
  }
};

/**
 * Create a new conversation
 * 
 * For authenticated users:
 * - JWT's sub claim establishes identity (cross-device continuity)
 * - routingAttributes provide profile info for agents (email, name, dealerId, etc.)
 * - Salesforce automatically links to existing conversation if user has one
 * 
 * @param {Object} routingAttributes - User profile and routing info for agents
 * @param {string} existingConversationId - Optional existing conversation ID to resume/reopen
 */
export const createConversation = async (routingAttributes = {}, existingConversationId = null) => {
  try {
    const messagingUrl = getMessagingUrl();
    if (!messagingUrl) {
      throw new Error('Missing messaging URL for conversation creation');
    }

    const developerName = getDeploymentDeveloperName();
    if (!developerName) {
      console.error('Salesforce Configuration Debug:');
      console.error('- messagingUrl:', messagingUrl);
      console.error('- developerName:', developerName);
      console.error('- All storage:', {
        jwt: getJwt(),
        orgId: getOrganizationId(),
        conversationId: getConversationId()
      });
      throw new Error('Missing deployment developer name for conversation creation. Please ensure Salesforce service is properly initialized.');
    }

    // Generate a proper UUID like the sample app
    const generateUUID = () => {
      const hexDigits = "0123456789abcdef";
      const valueArray = new Uint32Array(32);
      crypto.getRandomValues(valueArray);

      let res = '';
      for (let i = 0; i < 32; i++) {
        if (i === 8 || i === 12 || i === 16 || i === 20) {
          res += '-';
        }
        if (i === 12) {
          res += '4'; // UUID version
        } else if (i === 16) {
          res += hexDigits.charAt((valueArray[i] & 0x3) | 0x8);
        } else {
          res += hexDigits.charAt(valueArray[i] & 0xf);
        }
      }
      return res;
    };

    // Use existing conversationId if provided, otherwise generate new UUID
    let conversationId = existingConversationId || generateUUID();
    
    if (existingConversationId) {
      console.log('[Salesforce:Conversation] Creating conversation with existing ID:', existingConversationId);
    } else {
      console.log('[Salesforce:Conversation] Creating new conversation with generated ID:', conversationId);
    }

    const url = `${messagingUrl}${SALESFORCE_CONSTANTS.API_PATHS.CONVERSATION}`;
    
    // Match the sample app's request structure exactly
    const requestBody = {
      esDeveloperName: developerName,
      conversationId: conversationId,
      ...(routingAttributes && { routingAttributes })
    };
    
    // Get JWT token directly like sample app
    const jwt = getCurrentJwt();
    if (!jwt) {
      throw new Error('No JWT token available for conversation creation');
    }
    
    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { message: `HTTP ${response.status}`, status: response.status };
      }
      console.error('[Salesforce:Conversation] Creation failed:', errorData);
      throw new Error(`Conversation creation failed: ${response.status} - ${JSON.stringify(errorData)}`);
    }
    
    // Parse response - Salesforce may return existing conversation for this user
    let responseData = {};
    try {
      responseData = await response.json();
      console.log('[Salesforce:Conversation] Create response:', responseData);
    } catch (e) {
      console.warn('[Salesforce:Conversation] No JSON response body');
    }
    
    // Use conversationId from response if provided (existing conversation)
    // Otherwise use the one we generated (new conversation)
    const finalConversationId = responseData?.conversationId || conversationId;
    const isExistingConversation = responseData?.conversationId && responseData.conversationId !== conversationId;
    
    // Store conversation ID for future use
    storeConversationId(finalConversationId);
    
    return {
      success: true,
      conversationId: finalConversationId,
      isExisting: isExistingConversation,
      responseData
    };
  } catch (error) {
    console.error('[Salesforce:Conversation] Failed to create:', error);
    throw error;
  }
};

/**
 * Send a text message to the conversation
 */
export const sendMessage = async (messageText, conversationId = null) => {
  try {
    const convId = conversationId || getConversationId();
    if (!convId) {
      throw new Error('No conversation ID available for sending message');
    }

    console.log('[Salesforce:Conversation] 📤 Sending message to conversation:', convId);
    console.log('[Salesforce:Conversation] 📤 Message text:', messageText);

    const messagingUrl = getMessagingUrl();
    if (!messagingUrl) {
      throw new Error('Missing messaging URL for sending message');
    }

    const url = `${messagingUrl}${SALESFORCE_CONSTANTS.API_PATHS.CONVERSATION_MESSAGE.replace('{conversationId}', convId)}`;
    console.log('[Salesforce:Conversation] 📤 API URL:', url);
    
    const messageId = generateUUID();
    const esDeveloperName = getDeploymentDeveloperName() || 'default';
    
    const requestBody = {
      message: {
        id: messageId,
        messageType: 'StaticContentMessage',
        staticContent: {
          formatType: 'Text',
          text: messageText
        }
      },
      esDeveloperName
    };
    
    console.log('[Salesforce:Conversation] 📤 Request body:', JSON.stringify(requestBody, null, 2));
    
    // Check JWT token before sending
    const jwt = getCurrentJwt();
    console.log('[Salesforce:Conversation] 📤 JWT available:', !!jwt);
    if (jwt) {
      try {
        const parts = jwt.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          console.log('[Salesforce:Conversation] 📤 JWT sub:', payload.sub);
          console.log('[Salesforce:Conversation] 📤 JWT exp:', new Date(payload.exp * 1000).toISOString());
          console.log('[Salesforce:Conversation] 📤 JWT expired:', payload.exp * 1000 < Date.now());
        }
      } catch (e) {
        console.warn('[Salesforce:Conversation] Could not decode JWT');
      }
    }
    
    const response = await sendAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });

    console.log('[Salesforce:Conversation] ✅ Message sent successfully');
    return response;
  } catch (error) {
    console.error('[Salesforce:Conversation] ❌ Failed to send message:', error);
    console.error('[Salesforce:Conversation] ❌ Error details:', {
      name: error.name,
      message: error.message,
      status: error.status,
      errorData: error.errorData
    });
    throw error;
  }
};

/**
 * Generate a UUID (matching sample app implementation)
 */
const generateUUID = () => {
  const hexDigits = "0123456789abcdef";
  const valueArray = new Uint32Array(32);
  crypto.getRandomValues(valueArray);

  let res = '';
  for (let i = 0; i < 32; i++) {
    if (i === 8 || i === 12 || i === 16 || i === 20) {
      res += '-';
    }
    if (i === 12) {
      res += '4'; // UUID version
    } else if (i === 16) {
      res += hexDigits.charAt((valueArray[i] & 0x3) | 0x8); // Bits need to start with 10
    } else {
      res += hexDigits.charAt(valueArray[i] & 0xf);
    }
  }
  return res;
};

/**
 * Send typing indicator
 */
export const sendTypingIndicator = async (isTyping = true, conversationId = null) => {
  try {
    const convId = conversationId || getConversationId();
    if (!convId) {
      console.debug('[Salesforce:Typing] No conversation ID available for typing indicator');
      return null;
    }

    const messagingUrl = getMessagingUrl();
    if (!messagingUrl) {
      console.debug('[Salesforce:Typing] Missing messaging URL for typing indicator');
      return null;
    }

    const jwt = getJwt();
    if (!jwt) {
      console.debug('[Salesforce:Typing] No JWT available for typing indicator');
      return null;
    }

    // Use the correct endpoint structure matching sample app
    const url = `${messagingUrl}/iamessage/api/v2/conversation/${convId}/entry`;
    
    const requestBody = {
      entryType: isTyping ? 
        SALESFORCE_CONSTANTS.ENTRY_TYPES.CONVERSATION_TYPING_STARTED_INDICATOR : 
        SALESFORCE_CONSTANTS.ENTRY_TYPES.CONVERSATION_TYPING_STOPPED_INDICATOR,
      id: generateUUID()
    };
    
    const response = await sendAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });

    return response;
  } catch (error) {
    // Typing indicators are not critical - fail silently
    // Only log in debug mode to avoid cluttering console
    console.debug('[Salesforce:Typing] Failed to send typing indicator:', error.message);
    return null;
  }
};

/**
 * List conversations (if needed for history)
 * Implements pagination: fetches up to 50 conversations per call,
 * continues fetching if more exist using startTimestamp parameter
 */
export const listConversations = async (includeClosedConversations = false, limit = 20) => {
  try {
    console.log('[Salesforce:Conversation] listConversations called with:', { includeClosedConversations, limit });
    
    const messagingUrl = getMessagingUrl();
    if (!messagingUrl) {
      throw new Error('Missing messaging URL for listing conversations');
    }

    // Check JWT before making request
    const jwtBeforeRequest = getJwt();
    console.log('[Salesforce:Conversation] JWT available before listConversations request:', !!jwtBeforeRequest);
    if (jwtBeforeRequest) {
      console.log('[Salesforce:Conversation] JWT length:', jwtBeforeRequest.length);
    }

    const PAGE_SIZE = 50;
    const MAX_PAGES = 50; // Safety limit to prevent infinite loops
    const allConversationsMap = new Map(); // Use Map to track unique conversations by ID
    let hasMoreData = true;
    let startTimestamp = null;
    let pageCount = 0;

    while (hasMoreData && pageCount < MAX_PAGES) {
      pageCount++;
      
      // Build URL with pagination params
      let url = `${messagingUrl}/iamessage/api/v2/conversation/list?inclClosedConvs=true&limit=${PAGE_SIZE}`;
      if (startTimestamp) {
        url += `&startTimestamp=${startTimestamp}`;
      }
      
      console.log(`[Salesforce:Conversation] listConversations page ${pageCount} URL:`, url);
      
      const response = await sendAuthenticatedRequest(url, {
        method: 'GET'
      });

      const conversations = response?.conversations || [];
      console.log(`[Salesforce:Conversation] listConversations page ${pageCount} returned ${conversations.length} conversations`);

      // Count how many NEW conversations we got (not already in our map)
      let newConversationsCount = 0;
      conversations.forEach(conv => {
        if (!allConversationsMap.has(conv.conversationId)) {
          allConversationsMap.set(conv.conversationId, conv);
          newConversationsCount++;
        }
      });

      console.log(`[Salesforce:Conversation] Page ${pageCount}: ${newConversationsCount} new conversations added`);

      // Stop conditions:
      // 1. Less than PAGE_SIZE returned - no more data on server
      // 2. No new conversations added - we're getting duplicates (API returning same data)
      // 3. conversations array is empty
      if (conversations.length < PAGE_SIZE) {
        console.log(`[Salesforce:Conversation] Received ${conversations.length} < ${PAGE_SIZE}, pagination complete`);
        hasMoreData = false;
      } else if (newConversationsCount === 0) {
        console.log('[Salesforce:Conversation] No new conversations in this page, stopping pagination');
        hasMoreData = false;
      } else {
        // API returns conversations sorted from newest to oldest
        // So the LAST item in the array is the oldest one
        // Use its startTimestamp for the next page
        const lastConversation = conversations[conversations.length - 1];
        const lastTimestamp = lastConversation?.startTimestamp;
        
        console.log(`[Salesforce:Conversation] Last item in response - conversationId: ${lastConversation?.conversationId}, startTimestamp: ${lastTimestamp}`);
        
        if (!lastTimestamp) {
          console.log('[Salesforce:Conversation] No startTimestamp found on last item, stopping pagination');
          hasMoreData = false;
        } else {
          const newStartTimestamp = lastTimestamp - 1;
          
          // Also check if startTimestamp didn't change (would cause infinite loop)
          if (newStartTimestamp === startTimestamp) {
            console.log('[Salesforce:Conversation] startTimestamp unchanged, stopping pagination');
            hasMoreData = false;
          } else {
            startTimestamp = newStartTimestamp;
            console.log(`[Salesforce:Conversation] More data available, next startTimestamp: ${startTimestamp} (last was ${lastTimestamp})`);
          }
        }
      }
    }

    if (pageCount >= MAX_PAGES) {
      console.warn(`[Salesforce:Conversation] Reached max page limit (${MAX_PAGES}), stopping pagination`);
    }

    // Convert Map to array
    const uniqueConversations = Array.from(allConversationsMap.values());

    console.log(`[Salesforce:Conversation] listConversations total: ${uniqueConversations.length} unique conversations from ${pageCount} page(s)`);
    console.log('[Salesforce:Conversation] listConversations response:', JSON.stringify({ conversations: uniqueConversations }).substring(0, 200));
    
    return { conversations: uniqueConversations };
  } catch (error) {
    console.error('[Salesforce:Conversation] Failed to list conversations:', error);
    console.error('[Salesforce:Conversation] listConversations error details:', error.message);
    throw error;
  }
};

/**
 * List conversation entries (message history)
 */
export const listConversationEntries = async (conversationId = null, limit = 50) => {
  try {
    const convId = conversationId || getConversationId();
    if (!convId) {
      throw new Error('No conversation ID available for listing entries');
    }

    const messagingUrl = getMessagingUrl();
    if (!messagingUrl) {
      throw new Error('Missing messaging URL for listing conversation entries');
    }

    const url = `${messagingUrl}/iamessage/api/v2/conversation/${convId}/entries?limit=${limit}`;
    
    const response = await sendAuthenticatedRequest(url, {
      method: 'GET'
    });

    return response;
  } catch (error) {
    console.error('Failed to list conversation entries:', error);
    throw error;
  }
};

/**
 * Get current conversation ID
 */
export const getCurrentConversationId = () => {
  return getConversationId();
};

/**
 * Check if conversation is active
 */
export const hasActiveConversation = () => {
  return !!getConversationId();
};

/**
 * Get conversation status
 * Calls GET /iamessage/api/v2/conversation/{conversationId} to check conversation status
 * 
 * @param {string} conversationId - Optional conversation ID (uses stored ID if not provided)
 * @returns {Promise<Object>} Conversation details with status
 */
export const getConversationStatus = async (conversationId = null) => {
  try {
    const convId = conversationId || getConversationId();
    if (!convId) {
      throw new Error('No conversation ID available');
    }

    const jwt = getJwt();
    if (!jwt) {
      throw new Error('No JWT token available');
    }

    const messagingUrl = getMessagingUrl();
    if (!messagingUrl) {
      throw new Error('Missing messaging URL');
    }

    const url = `${messagingUrl}/iamessage/api/v2/conversation/${convId}`;
    
    console.log('[Salesforce:Conversation] Checking conversation status:', convId);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get conversation status: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[Salesforce:Conversation] Conversation status response:', data);
    
    return data;
  } catch (error) {
    console.error('[Salesforce:Conversation] Failed to get conversation status:', error);
    throw error;
  }
};

/**
 * Get conversation entries with proper filtering
 * This is the recommended function for page reload restoration
 * 
 * @param {string} conversationId - The conversation ID to fetch entries for
 * @param {Object} options - Options for filtering
 * @param {number} options.limit - Maximum number of entries to fetch (default: 50)
 * @param {string} options.entryTypeFilter - Filter by entry type (default: 'Message' - only messages)
 * @returns {Promise<Object>} Response with conversationEntries array
 */
export const getConversationEntries = async (conversationId, options = {}) => {
  try {
    const {
      limit = 50,
      entryTypeFilter = 'Message'
    } = options;

    if (!conversationId) {
      throw new Error('conversationId is required for getConversationEntries');
    }

    const messagingUrl = getMessagingUrl();
    if (!messagingUrl) {
      throw new Error('Missing messaging URL');
    }

    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      entryTypeFilter
    });

    const url = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/entries?${queryParams}`;
    
    const response = await sendAuthenticatedRequest(url, {
      method: 'GET'
    });

    return response;
  } catch (error) {
    console.error('Failed to get conversation entries:', error);
    
    throw error;
  }
};

/**
 * Transform conversation entries from API format to SSE format
 * API returns parsed objects, but EventSource sends entryPayload as string
 */
export const transformConversationEntries = (entries, conversationId) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries.map((entry) => ({
    conversationId,
    conversationEntry: {
      ...entry,
      entryPayload: typeof entry.entryPayload === 'string' 
        ? entry.entryPayload 
        : JSON.stringify(entry.entryPayload)
    }
  }));
};

/**
 * Parse SSE-formatted entries for Redux store
 */
export const parseEntriesForRedux = (sseFormattedEntries) => {
  if (!Array.isArray(sseFormattedEntries) || sseFormattedEntries.length === 0) {
    return [];
  }

  const orderedEntries = [...sseFormattedEntries].reverse();
  const messages = [];

  orderedEntries.forEach((item, index) => {
    try {
      const { conversationEntry } = item;
      
      const payload = typeof conversationEntry.entryPayload === 'string'
        ? JSON.parse(conversationEntry.entryPayload)
        : conversationEntry.entryPayload;

      const messageText = payload.abstractMessage?.staticContent?.text || 
                         payload.text || 
                         payload.message || 
                         '';
      
      const entryType = payload.abstractMessage?.messageType || 'StaticContentMessage';
      const senderRole = conversationEntry.sender?.role || 'Unknown';
      const senderDisplayName = conversationEntry.senderDisplayName || 'Guest';
      const isEndUserMessage = senderRole === 'EndUser';
      
      const message = {
        id: conversationEntry.identifier || `msg_${Date.now()}_${index}`,
        text: messageText,
        message: messageText,
        timestamp: conversationEntry.transcriptedTimestamp || 
                   conversationEntry.clientTimestamp || 
                   Date.now(),
        sender: senderRole,
        senderDisplayName,
        senderRole,
        isEndUserMessage,
        actorType: senderRole,
        conversationId: item.conversationId,
        entryType,
        type: 'salesforce',
        sequenceNumber: index + 1,
        isRestored: true,
        payload,
        originalEntry: conversationEntry
      };

      messages.push(message);
      
    } catch (error) {
      console.error(`Failed to parse entry ${index}:`, error);
    }
  });

  return messages;
};

/**
 * Complete restoration flow helper
 * Combines: getContinuityJwt → listConversations → getConversationEntries → transform → parse
 */
export const restoreConversationData = async () => {
  try {
    const { getContinuityJwt } = await import('./authService');
    const { validateRestorationData, getConversationId: getStoredConvId } = await import('./webStorageService');
    
    const validation = validateRestorationData();
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.error} - ${validation.message}`);
    }
    
    const conversationId = getStoredConvId();
    await getContinuityJwt();
    
    const convList = await listConversations(false);
    const foundConversation = convList.conversations?.find(
      (conv) => conv.conversationId === conversationId
    );
    
    if (!foundConversation) {
      throw new Error('CONVERSATION_NOT_FOUND: Conversation not found in list');
    }
    
    const entriesResponse = await getConversationEntries(conversationId, {
      limit: 50,
      entryTypeFilter: 'Message'
    });
    
    const transformed = transformConversationEntries(
      entriesResponse.conversationEntries || [],
      conversationId
    );
    
    const messages = parseEntriesForRedux(transformed);
    
    return {
      messages,
      conversationId,
      status: foundConversation.status || 'ACTIVE',
      conversationDetails: foundConversation
    };
    
  } catch (error) {
    console.error('Conversation restoration failed:', error);
    throw error;
  }
};

/**
 * Find existing conversation for authenticated user
 * 
 * ============================================================================
 * CROSS-DEVICE CONVERSATION LOOKUP
 * ============================================================================
 * 
 * For authenticated users, Salesforce links conversations to the verified user
 * (identified by Cognito sub claim in the JWT).
 * 
 * This function:
 * 1. Calls GET /conversation/list to get all conversations for this user
 * 2. Finds the most recent ACTIVE conversation (if any)
 * 3. Returns conversation details and history
 * 
 * CROSS-DEVICE FLOW:
 * - User logs in on Device B (no local conversationId stored)
 * - We authenticate with Cognito JWT → get Salesforce Messaging Token
 * - Salesforce token contains verified user identity (Cognito sub)
 * - listConversations returns ALL conversations for this verified user
 * - We find the ACTIVE one and fetch its history
 * 
 * @returns {Promise<Object>} { hasConversation, conversationId?, messages[], status? }
 */
export const findUserConversation = async () => {
  try {
    console.log('[Salesforce:Conversation] Looking for 2 most recent conversations for this user...');
    
    // Get list of conversations for this authenticated user
    // The Messaging Access Token identifies the user via JWT sub claim
    const convList = await listConversations(false, 50); // Get more to find 2 recent ones
    
    console.log('[Salesforce:Conversation] listConversations response:', convList);
    
    const allConversations = convList?.conversations || [];
    
    // Filter conversations by esDeveloperName to only show conversations for current deployment
    const currentEsDeveloperName = getDeploymentDeveloperName();
    const conversations = allConversations.filter(conv => {
      // Include conversation if esDeveloperName matches current deployment
      // Exclude if esDeveloperName is null/undefined or doesn't match
      const convEsDeveloperName = conv.esDeveloperName;
      const isMatch = convEsDeveloperName && convEsDeveloperName === currentEsDeveloperName;
      if (!isMatch) {
        console.log('[Salesforce:Conversation] Filtering out conversation:', conv.conversationId, 
          '- esDeveloperName:', convEsDeveloperName, 'expected:', currentEsDeveloperName);
      }
      return isMatch;
    });
    
    console.log('[Salesforce:Conversation] Filtered from', allConversations.length, 'to', conversations.length, 'conversations for esDeveloperName:', currentEsDeveloperName);
    
    if (!conversations || conversations.length === 0) {
      console.log('[Salesforce:Conversation] No existing conversations found for this user');
      return {
        hasConversation: false,
        messages: [],
        recentConversations: []
      };
    }
    
    // Debug: Log all conversation details
    console.log('[Salesforce:Conversation] Found', conversations.length, 'conversations');
    conversations.forEach((conv, idx) => {
      console.log(`[Salesforce:Conversation] Conv ${idx}:`, {
        id: conv.conversationId,
        status: conv.status,
        endTimestamp: conv.endTimestamp,
        startTimestamp: conv.startTimestamp,
        isConversationClosed: conv.isConversationClosed,
        allKeys: Object.keys(conv)
      });
    });
    
    // Sort conversations by startTimestamp (most recent first)
    const sortedConversations = [...conversations].sort((a, b) => {
      const timeA = a.startTimestamp || 0;
      const timeB = b.startTimestamp || 0;
      return timeB - timeA; // Descending order (newest first)
    });
    
    // Check if latest conversation is closed
    const latestConversation = sortedConversations[0];
    const isLatestClosed = latestConversation.isConversationClosed === true || latestConversation.isConversationClosed === 'true';
    
    // Get 2 conversations if latest is open, 1 if closed
    const numberOfConversationsToFetch = isLatestClosed ? 1 : 2;
    const conversationsToFetch = sortedConversations.slice(0, numberOfConversationsToFetch);
    
    console.log('[Salesforce:Conversation] Latest conversation closed:', isLatestClosed);
    console.log('[Salesforce:Conversation] ⭐ Will fetch history for', conversationsToFetch.length, 'conversations:', conversationsToFetch.map(c => c.conversationId));
    
    // Fetch history for conversations in parallel for better performance
    const allMessages = [];
    const conversationDetails = [];
    
    // Create promises for fetching history
    const fetchPromises = conversationsToFetch.map(async (conv, index) => {
      console.log(`[Salesforce:Conversation] 🔄 API Call ${index + 1}/${conversationsToFetch.length} - Fetching history for conversation:`, conv.conversationId);
      
      try {
        const entriesResponse = await getConversationEntries(conv.conversationId, {
          limit: 50,
          entryTypeFilter: 'Message'
        });
        
        console.log(`[Salesforce:Conversation] ✅ API Call ${index + 1} COMPLETED - Retrieved ${entriesResponse?.conversationEntries?.length || 0} entries for`, conv.conversationId);
        
        if (entriesResponse?.conversationEntries?.length > 0) {
          const transformed = transformConversationEntries(
            entriesResponse.conversationEntries,
            conv.conversationId
          );
          const messages = parseEntriesForRedux(transformed);
          
          // Add conversation separator info to first message of each conversation
          if (messages.length > 0) {
            messages[0].isConversationStart = true;
            messages[0].conversationStartTime = conv.startTimestamp;
          }
          
          return {
            success: true,
            messages,
            details: {
              conversationId: conv.conversationId,
              status: conv.status,
              startTimestamp: conv.startTimestamp,
              endTimestamp: conv.endTimestamp,
              messageCount: messages.length
            }
          };
        } else {
          console.log(`[Salesforce:Conversation] ℹ️ API Call ${index + 1} - No messages in conversation:`, conv.conversationId);
          return {
            success: true,
            messages: [],
            details: {
              conversationId: conv.conversationId,
              status: conv.status,
              startTimestamp: conv.startTimestamp,
              endTimestamp: conv.endTimestamp,
              messageCount: 0
            }
          };
        }
      } catch (historyError) {
        console.error(`[Salesforce:Conversation] ❌ API Call ${index + 1} FAILED for conversation`, conv.conversationId, ':', historyError);
        return {
          success: false,
          error: historyError,
          conversationId: conv.conversationId
        };
      }
    });
    
    // Wait for all history fetches to complete
    console.log('[Salesforce:Conversation] ⏳ Waiting for all', fetchPromises.length, 'API calls to complete...');
    const results = await Promise.all(fetchPromises);
    console.log('[Salesforce:Conversation] ✅ All API calls completed. Results:', results.map(r => ({ 
      conversationId: r.conversationId || r.details?.conversationId, 
      success: r.success, 
      messageCount: r.messages?.length || 0 
    })));
    
    // Process results
    results.forEach((result, index) => {
      if (result.success && result.messages) {
        allMessages.push(...result.messages);
        if (result.details) {
          conversationDetails.push(result.details);
        }
        console.log(`[Salesforce:Conversation] ✅ Conversation ${index + 1}: Added ${result.messages.length} messages`);
      } else if (!result.success) {
        console.warn(`[Salesforce:Conversation] ⚠️ Conversation ${index + 1}: Failed to fetch history`);
      }
    });
    
    // Sort all messages by timestamp (oldest first for display)
    allMessages.sort((a, b) => {
      const timeA = a.timestamp || 0;
      const timeB = b.timestamp || 0;
      return timeA - timeB;
    });
    
    console.log('[Salesforce:Conversation] 📊 SUMMARY: Total messages from', conversationDetails.length, 'conversations:', allMessages.length);
    console.log('[Salesforce:Conversation] 📊 Conversations processed:', conversationDetails.map(d => `${d.conversationId} (${d.messageCount} msgs)`));
    
    // Check if there's an active conversation
    const activeConversation = conversationsToFetch.find(conv => {
      const status = (conv.status || '').toUpperCase();
      const isEndTimestampOpen = conv.endTimestamp === 0 || conv.endTimestamp === '0';
      const isNotClosed = conv.isConversationClosed === false || conv.isConversationClosed === 'false';
      
      return status === 'ACTIVE' || status === 'OPEN' || status === 'WAITING' ||
             isEndTimestampOpen || isNotClosed;
    });
    
    if (activeConversation) {
      console.log('[Salesforce:Conversation] Found ACTIVE conversation:', activeConversation.conversationId);
      storeConversationId(activeConversation.conversationId);
      
      return {
        hasConversation: true,
        conversationId: activeConversation.conversationId,
        messages: allMessages,
        status: activeConversation.status,
        conversationDetails: activeConversation,
        recentConversations: conversationDetails,
        totalConversationsCount: conversations.length
      };
    } else {
      console.log('[Salesforce:Conversation] No active conversation found, showing history only');
      
      return {
        hasConversation: false,
        messages: allMessages,
        recentConversations: conversationDetails,
        mostRecentConversationId: conversationsToFetch[0]?.conversationId,
        totalConversationsCount: conversations.length
      };
    }
  } catch (error) {
    console.error('[Salesforce:Conversation] Failed to find user conversations:', error);
    throw error;
  }
};

/**
 * Fetch history from the 2 most recent conversations
 * This function ALWAYS fetches fresh conversation list and history
 * Used when showing history in chat (including when creating new conversations)
 * 
 * @returns {Promise<Object>} Object with messages array and conversation details
 */
export const fetchRecentConversationsHistory = async () => {
  try {
    console.log('[Salesforce:History] 🔍 Fetching history from 2 most recent conversations...');
    
    // Step 1: Get fresh list of conversations
    const convList = await listConversations(false, 50);
    console.log('[Salesforce:History] ✅ Got conversation list:', convList?.conversations?.length, 'conversations');
    
    const allConversations = convList?.conversations || [];
    
    // Filter conversations by esDeveloperName to only show conversations for current deployment
    const currentEsDeveloperName = getDeploymentDeveloperName();
    const conversations = allConversations.filter(conv => {
      // Include conversation if esDeveloperName matches current deployment
      // Exclude if esDeveloperName is null/undefined or doesn't match
      const convEsDeveloperName = conv.esDeveloperName;
      return convEsDeveloperName && convEsDeveloperName === currentEsDeveloperName;
    });
    
    console.log('[Salesforce:History] Filtered from', allConversations.length, 'to', conversations.length, 'conversations for esDeveloperName:', currentEsDeveloperName);
    
    if (conversations.length === 0) {
      console.log('[Salesforce:History] No conversations found');
      return {
        messages: [],
        conversationDetails: []
      };
    }
    
    // Step 2: Sort by startTimestamp (newest first)
    const sortedConversations = [...conversations].sort((a, b) => {
      const timeA = a.startTimestamp || 0;
      const timeB = b.startTimestamp || 0;
      return timeB - timeA; // Descending order (newest first)
    });
    
    // Step 3: Check if latest conversation is closed
    const latestConversation = sortedConversations[0];
    const isLatestClosed = latestConversation.isConversationClosed === true || latestConversation.isConversationClosed === 'true';
    
    // Get 2 conversations if latest is open, 1 if closed
    const numberOfConversationsToFetch = isLatestClosed ? 1 : 2;
    const conversationsToFetch = sortedConversations.slice(0, numberOfConversationsToFetch);
    
    console.log('[Salesforce:History] Latest conversation closed:', isLatestClosed);
    console.log('[Salesforce:History] ⭐ Will fetch history from', conversationsToFetch.length, 'conversations:', 
      conversationsToFetch.map(c => c.conversationId));
    
    // Step 4: Fetch history in parallel
    const fetchPromises = conversationsToFetch.map(async (conv, index) => {
      console.log(`[Salesforce:History] 🔄 API Call ${index + 1}/${conversationsToFetch.length} - Getting entries for:`, conv.conversationId);
      
      try {
        const entriesResponse = await getConversationEntries(conv.conversationId, {
          limit: 50,
          entryTypeFilter: 'Message'
        });
        
        console.log(`[Salesforce:History] ✅ API Call ${index + 1} completed - ${entriesResponse?.conversationEntries?.length || 0} entries`);
        
        if (entriesResponse?.conversationEntries?.length > 0) {
          const transformed = transformConversationEntries(
            entriesResponse.conversationEntries,
            conv.conversationId
          );
          const messages = parseEntriesForRedux(transformed);
          
          // Mark first message of each conversation for UI separator
          if (messages.length > 0) {
            messages[0].isConversationStart = true;
            messages[0].conversationStartTime = conv.startTimestamp;
          }
          
          return {
            success: true,
            messages,
            details: {
              conversationId: conv.conversationId,
              status: conv.status,
              startTimestamp: conv.startTimestamp,
              endTimestamp: conv.endTimestamp,
              messageCount: messages.length
            }
          };
        }
        
        return {
          success: true,
          messages: [],
          details: {
            conversationId: conv.conversationId,
            status: conv.status,
            startTimestamp: conv.startTimestamp,
            endTimestamp: conv.endTimestamp,
            messageCount: 0
          }
        };
      } catch (error) {
        console.error(`[Salesforce:History] ❌ Failed to fetch history for ${conv.conversationId}:`, error);
        return {
          success: false,
          conversationId: conv.conversationId,
          error
        };
      }
    });
    
    // Step 5: Wait for all fetches
    const results = await Promise.all(fetchPromises);
    
    // Step 6: Combine all messages
    const allMessages = [];
    const conversationDetails = [];
    
    results.forEach((result, index) => {
      if (result.success && result.messages) {
        allMessages.push(...result.messages);
        if (result.details) {
          conversationDetails.push(result.details);
        }
        console.log(`[Salesforce:History] ✅ Conversation ${index + 1}: ${result.messages.length} messages added`);
      }
    });
    
    // Step 7: Sort all messages by timestamp (oldest first)
    allMessages.sort((a, b) => {
      const timeA = a.timestamp || 0;
      const timeB = b.timestamp || 0;
      return timeA - timeB;
    });
    
    console.log('[Salesforce:History] 📊 TOTAL:', allMessages.length, 'messages from', conversationDetails.length, 'conversations');
    
    return {
      messages: allMessages,
      conversationDetails
    };
  } catch (error) {
    console.error('[Salesforce:History] ❌ Failed to fetch recent conversations history:', error);
    return {
      messages: [],
      conversationDetails: []
    };
  }
};

/**
 * Get end-user information including active conversation
 * 
 * ⚠️ NOTE: This endpoint may return 404 in some Salesforce environments.
 * Cross-device continuity still works via JWT sub claim when creating conversations.
 * This function is kept for debugging and environments where it IS available.
 * 
 * This endpoint is for userId-based (cross-device) conversation management.
 * Salesforce uses the JWT's sub claim to identify the verified user and returns
 * their active conversation (if any).
 * 
 * Sample response (when available):
 * {
 *   "verifiedUser": {
 *     "uid": "b21ea823-9891-4280-bf82-83e5afa74d5e",  // Cognito sub
 *     "verificationType": "Cognito_Verification"
 *   },
 *   "activeConversation": {
 *     "conversationId": "...",
 *     "status": "ACTIVE" | "ENDED"
 *   }
 * }
 * 
 * @returns {Promise<Object>} End-user data including verifiedUser and activeConversation
 * @throws {Error} May throw 404 if endpoint not available in your Salesforce org
 */
export const getEndUser = async () => {
  try {
    const messagingUrl = getMessagingUrl();
    if (!messagingUrl) {
      throw new Error('Missing messaging URL');
    }

    const url = `${messagingUrl}/iamessage/api/v2/end-user`;
    
    console.log('[Salesforce:Conversation] Calling GET /end-user (may not be available in all orgs)...');
    
    const response = await sendAuthenticatedRequest(url, {
      method: 'GET'
    });

    console.log('[Salesforce:Conversation] /end-user response:', JSON.stringify(response, null, 2));
    
    return response;
  } catch (error) {
    console.error('[Salesforce:Conversation] Failed to get end-user:', error);
    throw error;
  }
};

/**
 * Get conversation history for authenticated user by userId (via JWT sub claim)
 * 
 * This is the PRIMARY method for cross-device conversation lookup.
 * Instead of requiring a stored conversationId, this method:
 * 1. Calls /end-user to get the active conversation for the authenticated user
 * 2. Fetches conversation history if an active conversation exists
 * 
 * CROSS-DEVICE FLOW:
 * - User logs in on Device B (no local conversationId)
 * - JWT contains same Cognito sub as Device A
 * - Salesforce maps to same verified user
 * - /end-user returns the existing conversation from Device A
 * - History is fetched and displayed
 * 
 * @returns {Promise<Object>} { hasConversation, conversationId?, messages[], verifiedUser, conversationStatus? }
 */
export const getUserConversationHistory = async () => {
  try {
    // Get end-user info which includes active conversation (by userId via JWT)
    const endUserData = await getEndUser();
    
    const verifiedUser = endUserData?.verifiedUser;
    console.log('[Salesforce:Conversation] Verified user:', verifiedUser);
    
    // Check if user has an active conversation
    const activeConversation = endUserData?.activeConversation;
    const conversationId = activeConversation?.conversationId;
    const conversationStatus = activeConversation?.status;
    
    if (!conversationId) {
      console.log('[Salesforce:Conversation] No active conversation for this user');
      return {
        hasConversation: false,
        messages: [],
        verifiedUser
      };
    }
    
    console.log('[Salesforce:Conversation] Found active conversation:', conversationId, 'Status:', conversationStatus);
    
    // If conversation is ENDED, we may want to create a new one instead
    if (conversationStatus === 'ENDED') {
      console.log('[Salesforce:Conversation] Conversation is ENDED - will create new');
      return {
        hasConversation: false,
        messages: [],
        verifiedUser,
        previousConversationId: conversationId,
        previousConversationStatus: conversationStatus
      };
    }

    // Store the conversationId for future use
    storeConversationId(conversationId);

    // Fetch conversation history
    console.log('[Salesforce:Conversation] Fetching conversation history...');
    const entriesResponse = await getConversationEntries(conversationId, {
      limit: 50,
      entryTypeFilter: 'Message'
    });
    
    let messages = [];
    if (entriesResponse?.conversationEntries?.length > 0) {
      console.log('[Salesforce:Conversation] Retrieved', entriesResponse.conversationEntries.length, 'entries');
      const transformed = transformConversationEntries(
        entriesResponse.conversationEntries,
        conversationId
      );
      messages = parseEntriesForRedux(transformed);
      console.log('[Salesforce:Conversation] Parsed', messages.length, 'messages');
    } else {
      console.log('[Salesforce:Conversation] No message entries in conversation');
    }
    
    return {
      hasConversation: true,
      conversationId,
      messages,
      conversationStatus,
      verifiedUser
    };
  } catch (error) {
    console.error('[Salesforce:Conversation] Failed to get user conversation history:', error);
    throw error;
  }
};